import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { useMetaTags } from '../../services/useMetaTags';
import trackPageView from '../../services/trackPageView';
import {useAuth} from '../../services/use-auth';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import NotFoundPage from '../Errors/NotFound';
import '../../assets/styles/trainingCourse/coursePreview.scss';

function resolveLocalePrefix(pathname) {
    const match = pathname.match(/^\/(en|uk)(?=\/|$)/);

    return match ? `/${match[1]}` : '';
}

function withLocalePrefix(path, localePrefix) {
    if (!path) {
        return path;
    }

    if (!localePrefix || /^https?:\/\//.test(path)) {
        return path;
    }

    if (path === localePrefix || path.startsWith(`${localePrefix}/`)) {
        return path;
    }

    return `${localePrefix}${path.startsWith('/') ? path : `/${path}`}`;
}

function normalizeUrl(value) {
    if (!value) {
        return '/img/fallback-min.png';
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

function authorFullName(author) {
    if (!author) {
        return '';
    }

    if (author.fullName) {
        return author.fullName.trim();
    }

    return [author.lastName, author.firstName, author.middleName].filter(Boolean).join(' ').trim();
}

export default function TrainingCoursePreviewPage({isTesting = false}) {
    const {id} = useParams();
    const {t} = useTranslation();
    const history = useHistory();
    const {pathname} = useLocation();
    const auth = useAuth();
    const [previewData, setPreviewData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [startPending, setStartPending] = useState(false);
    const [bonusPending, setBonusPending] = useState(false);
    const [bonusErrorVisible, setBonusErrorVisible] = useState(false);
    const [promoCode, setPromoCode] = useState('');
    const [promoError, setPromoError] = useState('');
    const [publicPopupVisible, setPublicPopupVisible] = useState(false);
    const [isNotFound, setIsNotFound] = useState(false);
    const localePrefix = useMemo(() => resolveLocalePrefix(pathname), [pathname]);
    const textNamespace = isTesting ? 'testings' : 'courses';
    const previewEndpoint = isTesting
        ? `/api/v1/testings/preview/${id}`
        : `/api/v1/courses/preview/${id}`;

    const loadPreview = useCallback(() => {
        setIsLoading(true);
        setIsNotFound(false);

        fetch(previewEndpoint, {credentials: 'same-origin'})
            .then(async (response) => {
                if (response.status === 401) {
                    history.replace(withLocalePrefix('/account/login', localePrefix));
                    return null;
                }

                if (response.status === 404) {
                    setIsNotFound(true);
                    return null;
                }

                const payload = await response.json().catch(() => null);

                if (payload?.state === 'not_found') {
                    setIsNotFound(true);
                    return null;
                }

                if (response.status === 409 && payload?.redirectUrl) {
                    history.replace(withLocalePrefix(payload.redirectUrl, localePrefix));
                    return null;
                }

                if (!response.ok) {
                    throw new Error('Failed to load preview data');
                }

                return payload;
            })
            .then((payload) => {
                if (payload) {
                    setPreviewData(payload);
                } else {
                    setPreviewData(null);
                }
            })
            .catch(() => {
                setPreviewData(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [history, localePrefix, previewEndpoint]);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    const pageTitle = previewData?.course?.name || (isTesting ? t('testings.title', 'Testings') : t('courses.title'));

    useMetaTags({
        title: pageTitle,
        description: t('app.description'),
        metas: [{name: 'keywords', content: t('app.keywords')}],
    });

    useEffect(() => {
        if (previewData?.course?.name) {
            trackPageView(previewData.course.name);
        }
    }, [previewData]);

    if (isLoading) {
        return null;
    }

    if (isNotFound) {
        return <NotFoundPage />;
    }

    if (!previewData) {
        return (
            <section>
                <div className="container">
                    <div className="course-section">
                        <h6>{t('app.errors.errorOccurred', 'An error occurred')}</h6>
                    </div>
                </div>
            </section>
        );
    }

    const course = previewData.course || {};
    const access = previewData.access || {};
    const referenceInformation = previewData.referenceInformation || {};
    const actions = previewData.actions || {};
    const pricePolicy = previewData.pricePolicy;
    const isChargeable = !!access.isChargeable;
    const listPath = `${localePrefix}${isTesting ? '/testings' : '/courses'}`;
    const returnUrl = withLocalePrefix(actions.viewUrl || (isTesting ? `/testings/${course.id}` : `/course/${course.id}`), localePrefix);
    const loginUrl = withLocalePrefix(actions.loginUrl || '/account/login', localePrefix);
    const registrationUrl = withLocalePrefix(actions.registrationUrl || '/account/registration', localePrefix);
    const paymentUrl = withLocalePrefix(actions.paymentUrl || `/payment/course/${course.id}`, localePrefix);
    const certificateUrl = withLocalePrefix(actions.certificateUrl || `/course/${course.id}/certificate`, localePrefix);
    const authors = Array.isArray(course.authors) ? course.authors : [];
    const authorNames = authors.map(authorFullName).filter(Boolean).join(', ');
    const modules = Array.isArray(course.modules) ? course.modules : [];
    const specialities = Array.isArray(course.specialities) ? course.specialities : [];
    const developers = Array.isArray(course.developers) ? course.developers : [];
    const prices = Array.isArray(referenceInformation.prices) ? referenceInformation.prices : [];
    const complexityValue = Number(course.complexity || 1);
    const complexityMap = {
        1: `${textNamespace}.preview.complexity.low`,
        2: `${textNamespace}.preview.complexity.medium`,
        3: `${textNamespace}.preview.complexity.high`,
    };

    const onStartCourse = () => {
        if (startPending || !actions.startUrl) {
            return;
        }

        setStartPending(true);
        fetch(actions.startUrl, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to start course');
                }

                history.push(returnUrl);
            })
            .catch(() => null)
            .finally(() => {
                setStartPending(false);
            });
    };

    const onBuyByBonus = () => {
        if (bonusPending || !actions.bonusPaymentUrl) {
            return;
        }

        if (access.isPublic) {
            history.push(loginUrl);
            return;
        }

        setBonusPending(true);
        setBonusErrorVisible(false);

        fetch(actions.bonusPaymentUrl, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Bonus payment failed');
                }

                return loadPreview();
            })
            .catch(() => {
                setBonusErrorVisible(true);
            })
            .finally(() => {
                setBonusPending(false);
            });
    };

    const onPromoSubmit = (event) => {
        event.preventDefault();
        setPromoError('');

        if (!auth?.user) {
            history.push(loginUrl);
            return;
        }

        if (!promoCode) {
            setPromoError(t('app.promoCode.invalidCode'));
            return;
        }

        fetch(actions.promoActivateUrl || '/api/v1/promo/code/activate', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: new URLSearchParams({promoCode}).toString(),
        })
            .then((response) => {
                if (response.status === 202) {
                    setPromoCode('');
                    setPromoError('');
                    return loadPreview();
                }

                if (response.status === 406) {
                    setPromoError(t('app.promoCode.alreadyActivated'));
                    return null;
                }

                if (response.status === 410) {
                    setPromoError(t('app.promoCode.contentAlreadyActivated'));
                    return null;
                }

                setPromoError(t('app.promoCode.invalidCode'));
                return null;
            })
            .catch(() => {
                setPromoError(t('app.promoCode.invalidCode'));
            });
    };

    const showCourseProgram = !isTesting || isChargeable;
    const showBasePrice = Number(course.price || 0) !== 0 && !pricePolicy;
    const showPricePolicyPrice = !!pricePolicy && !pricePolicy.promoCodeOnly;
    const showBonusPrice = Number(course.bonusPrice || 0) !== 0;

    return (
        <section>
            <div className="container">
                {!isChargeable && <AdvertiseBlock unitId="coursePreview1" blockLocation="top" entityId={String(course.id || '')} />}
                <div className="row">
                    <nav className="breadcrumbs">
                        <ul>
                            <li>
                                <Link to={listPath}>
                                    {isTesting
                                        ? t('testings.preview.breadcrumbs', t('testings.tabs.all', 'All testings'))
                                        : t('courses.preview.breadcrumbs', t('courses.tabs.all'))}
                                </Link>
                            </li>
                            <li className="active" aria-current="page">{course.name}</li>
                        </ul>
                    </nav>
                </div>
            </div>

            <div className="container container-intro">
                <div className="course-intro" style={{backgroundImage: `url('${normalizeUrl(course.image)}')`}}>
                    <div className="inner-block flex-center">
                        <div className="course-intro-block">
                            <h1>{course.name}</h1>
                            <div className="authors">
                                <em>{t(`${textNamespace}.preview.authors`, t('courses.preview.authors'))}</em>{' '}
                                {authorNames}
                            </div>

                            {!isChargeable && (
                                <div className="btn-block">
                                    {access.isStarted ? (
                                        <Link to={returnUrl}>
                                            <button type="button" className="btn">
                                                {t(`${textNamespace}.preview.returnToCourse`, t('courses.preview.returnToCourse'))}
                                            </button>
                                        </Link>
                                    ) : (
                                        <>
                                            {access.isPublic ? (
                                                <button type="button" className="btn" onClick={() => setPublicPopupVisible((value) => !value)}>
                                                    {t(`${textNamespace}.preview.startCourse`, t('courses.preview.startCourse'))}
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    disabled={!access.isAvailableToUser || !access.isCourseStarted || startPending}
                                                    className="btn"
                                                    onClick={onStartCourse}>
                                                    {t(`${textNamespace}.preview.startCourse`, t('courses.preview.startCourse'))}
                                                </button>
                                            )}
                                        </>
                                    )}

                                    {publicPopupVisible && (
                                        <div className="public-popup-register-inline">
                                            <div className="button-block">
                                                <Link to={loginUrl} className="btn-register">{t('publicAuthPopup.login')}</Link>
                                            </div>
                                            <div className="button-block">
                                                <Link to={registrationUrl} className="btn-register">{t('publicAuthPopup.register')}</Link>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div
                                className="info"
                                dangerouslySetInnerHTML={{
                                    __html: access.isAvailableToUser
                                        ? (course.shortDescription || '')
                                        : (course.targetAudienceDisabledMessage || ''),
                                }} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="course-grid">
                    <div>
                        {isChargeable && (
                            <div className="course-section">
                                <div className="promotional-code-block">
                                    <h5>{t(`${textNamespace}.preview.payment.claimCourse`, t('courses.preview.payment.claimCourse'))}</h5>
                                    <div className="flex-container">
                                        <div className="column">
                                            {showBasePrice && (
                                                <div className="promo price">
                                                    <div className="wrap">
                                                        <div className="flex-baseline">
                                                            <div>
                                                                <h6>{t(`${textNamespace}.preview.payment.coursePrice`, t('courses.preview.payment.coursePrice'))}</h6>
                                                                <p className="price">
                                                                    {course.price} {t(`${textNamespace}.preview.payment.uah`, t('courses.preview.payment.uah'))}
                                                                </p>
                                                            </div>
                                                            <div className="buy-button-block">
                                                                <button type="button" className="btn js-buy-code" onClick={() => { window.location.href = paymentUrl; }}>
                                                                    {t(`${textNamespace}.preview.payment.buy`, t('courses.preview.payment.buy'))}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {showPricePolicyPrice && (
                                                <div className="promo price">
                                                    <div className="wrap">
                                                        <div className="flex-baseline">
                                                            <div>
                                                                <h6>{t(`${textNamespace}.preview.payment.coursePrice`, t('courses.preview.payment.coursePrice'))}</h6>
                                                                {Number(pricePolicy.finalPrice) === Number(pricePolicy.marketingPrice) ? (
                                                                    <p className="price">
                                                                        {pricePolicy.finalPrice} {t(`${textNamespace}.preview.payment.uah`, t('courses.preview.payment.uah'))}
                                                                    </p>
                                                                ) : (
                                                                    <>
                                                                        <p className="price-new">
                                                                            {pricePolicy.finalPrice} {t(`${textNamespace}.preview.payment.uah`, t('courses.preview.payment.uah'))}
                                                                        </p>
                                                                        <p className="list-price">
                                                                            <span className="price-block">
                                                                                {pricePolicy.marketingPrice} {t(`${textNamespace}.preview.payment.uah`, t('courses.preview.payment.uah'))}
                                                                            </span>
                                                                        </p>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="buy-button-block">
                                                                <button type="button" className="btn js-buy-code" onClick={() => { window.location.href = paymentUrl; }}>
                                                                    {Number(pricePolicy.finalPrice) === 0
                                                                        ? t(`${textNamespace}.preview.payment.get`, t('courses.preview.payment.get'))
                                                                        : t(`${textNamespace}.preview.payment.buy`, t('courses.preview.payment.buy'))}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {showBonusPrice && (
                                                <div className="promo js-bonus-buy">
                                                    <div className="wrap">
                                                        <div className="flex-baseline">
                                                            <div>
                                                                <h6>{t(`${textNamespace}.preview.payment.coursePrice`, t('courses.preview.payment.coursePrice'))}</h6>
                                                                <p className="price">
                                                                    {course.bonusPrice} {t(`${textNamespace}.preview.payment.bonus`, t('courses.preview.payment.bonus'))}
                                                                </p>
                                                            </div>
                                                            <div className="right-align">
                                                                <button type="button" className="btn" disabled={bonusPending} onClick={onBuyByBonus}>
                                                                    {t(`${textNamespace}.preview.payment.exchange`, t('courses.preview.payment.exchange'))}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={`error-block ${bonusErrorVisible ? '' : 'hide'}`}>
                                                        {t(`${textNamespace}.preview.payment.insufficientBonus`, t('courses.preview.payment.insufficientBonus'))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="column">
                                            <div className="promo activate">
                                                <form method="POST" id="promo-code-form" onSubmit={onPromoSubmit}>
                                                    <div className="wrap">
                                                        <div className="flex-baseline">
                                                            <div>
                                                                <h6>{t('app.promoCode.enterPromo')}</h6>
                                                                <input
                                                                    type="text"
                                                                    name="promoCode"
                                                                    placeholder="XX-XX-XX-XX"
                                                                    className="form-input js-promo-code"
                                                                    value={promoCode}
                                                                    onChange={(event) => setPromoCode(String(event.target.value || '').toUpperCase())} />
                                                            </div>
                                                            <div className="right-align">
                                                                <button type="submit" className="btn">{t('app.promoCode.activate')}</button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="error-block">
                                                        <div className={`promo-code-error ${promoError ? '' : 'hidden'}`}>{promoError}</div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="course-section">
                            <h6>{t(`${textNamespace}.preview.about`, t('courses.preview.about'))}</h6>
                            <p dangerouslySetInnerHTML={{__html: course.description || ''}} />
                        </div>

                        {showCourseProgram && (
                            <div className="course-section">
                                <h6>{t(`${textNamespace}.preview.program`, t('courses.preview.program'))}</h6>
                                <ol>
                                    {modules.map((module, index) => (
                                        <li key={`preview-module-${course.id}-${index}`}>{module.name}</li>
                                    ))}
                                    <li>{t(`${textNamespace}.preview.exam`, t('courses.preview.exam'))}</li>
                                </ol>
                            </div>
                        )}

                        <div className="course-section">
                            <h6>{t(`${textNamespace}.preview.courseAuthors`, t('courses.preview.courseAuthors'))}</h6>
                            {authors.map((author, index) => (
                                <div className="author-block" key={`preview-author-${course.id}-${index}`}>
                                    <div className="flex-center">
                                        {!!author.photo && (
                                            <div className="author-photo">
                                                <img src={normalizeUrl(author.photo)} className="img-fill" alt="" />
                                            </div>
                                        )}
                                        <div className="author-name">{authorFullName(author)}</div>
                                    </div>
                                    {!!author.description && (
                                        <div className="author-content">{author.description}</div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {!isChargeable && <AdvertiseBlock unitId="coursePreview1" blockLocation="bottom" entityId={String(course.id || '')} />}

                        {!!specialities.length && (
                            <div className="course-section">
                                <h6>{t(`${textNamespace}.preview.specialities`, t('courses.preview.specialities'))}</h6>
                                <div className="course-specialty flex wrap-flex">
                                    {specialities.map((speciality) => (
                                        <div className="specialty-block" key={`preview-speciality-${course.id}-${speciality.id}`}>
                                            <img src={normalizeUrl(speciality.image)} alt="" />
                                            <p className="name">{speciality.name}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {!access.isPublic && !!developers.length && (
                            <div className="course-section">
                                <h6>{course.developersTitle || t(`${textNamespace}.preview.sponsorsTitle`, t('courses.preview.sponsorsTitle'))}</h6>
                                <div className="developers-logo">
                                    {developers.map((developer, index) => (
                                        <div key={`preview-developer-${course.id}-${index}`}>
                                            {developer.imageLink ? (
                                                <a href={developer.imageLink} target="_blank" rel="noreferrer">
                                                    <img src={normalizeUrl(developer.image)} alt="" />
                                                </a>
                                            ) : (
                                                <img src={normalizeUrl(developer.image)} alt="" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="js-side-block">
                        <div>
                            <div className="reference-information">
                                <div className="inner-block">
                                    <h5>{t(`${textNamespace}.preview.complexity.title`, t('courses.preview.complexity.title'))}</h5>
                                    <div className="description">
                                        <i className={`icon-level-${course.complexity}`}></i>{' '}
                                        {t(complexityMap[complexityValue] || `${textNamespace}.preview.complexity.low`, t('courses.preview.complexity.low'))}
                                    </div>
                                </div>
                                <div className="inner-block">
                                    <h5>{t(`${textNamespace}.preview.certification.title`, t('courses.preview.certification.title'))}</h5>
                                    <div className="description">
                                        <i className="icon-certificate"></i>{' '}
                                        {course.hasCertificate
                                            ? t(`${textNamespace}.preview.certification.certified`, t('courses.preview.certification.certified'))
                                            : t(`${textNamespace}.preview.certification.notCertified`, t('courses.preview.certification.notCertified'))}
                                    </div>
                                    {access.allowToDownloadCertificate && (
                                        <div>
                                            <button type="button" className="btn small-text" onClick={() => history.push(certificateUrl)}>
                                                {t(`${textNamespace}.preview.certification.view`, t('courses.preview.certification.view'))}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="inner-block">
                                    <h5>{t(`${textNamespace}.preview.duration.title`, t('courses.preview.duration.title'))}</h5>
                                    <div className="description">
                                        {referenceInformation.duration} {t(`${textNamespace}.preview.duration.minutes`, t('courses.preview.duration.minutes'))}
                                    </div>
                                </div>
                                <div className="inner-block">
                                    <h5>{t(`${textNamespace}.preview.bounty`, t('courses.preview.bounty'))}</h5>
                                    <div className="score-inline">
                                        {prices.map((price, index) => (
                                            <span className={`score score-${price.code}`} key={`preview-price-${course.id}-${price.code}-${index}`}>
                                                {price.price}
                                                <span>{t(`app.currencies.${price.code}`)}</span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {!isChargeable && <AdvertiseBlock unitId="coursePreview3" blockLocation="sidebar" entityId={String(course.id || '')} />}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
